/**
 * The address line that finishes the address for you.
 *
 * Aaron: "how do we build in Google address autocomplete that suggests and
 * auto-populates as you type". This is the half in the browser; the key and
 * the vendor call are on the server (`server/src/services/places.ts`), so the
 * key is never in the page.
 *
 * It is a text box first and a lookup second, and the order matters. A
 * property in a new development, a farm lane, a site Google has never heard
 * of — all of those have to be typeable, so nothing here refuses what was
 * written or replaces it on blur. The menu is an offer: take one and the
 * other four boxes fill themselves in, ignore it and you have a plain text
 * field that behaved exactly as it did before.
 *
 * **With no `GOOGLE_MAPS_API_KEY` it is a plain text field**, silently. The
 * server says once whether this deployment has a key; without one the menu
 * never opens and no request is ever made. Nothing about adding a property
 * depends on a vendor being configured — §2.5's portability rule cuts this
 * way too.
 *
 * ## The session token
 *
 * Google bills autocomplete per request *unless* a run of keystrokes and the
 * details call that ends it share a token, in which case the run is billed
 * once. So: one token when somebody starts typing an address, the same token
 * on every keystroke after, the same token on the details call, and a fresh
 * one next time. Getting this wrong is not a bug you can see — it is a bill.
 */
import {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react'
import { createPortal } from 'react-dom'
import { uuidv7 } from 'uuidv7'
import { cn } from '../../lib/cn.js'
import { placeFloating } from '../../lib/place-floating.js'
import { request } from '../../lib/api.js'
import { TextInput } from './inputs.js'
import { IconPin } from '../../frame/icons.js'

/** What a chosen suggestion fills in. */
export interface ChosenAddress {
  line1: string
  city: string
  state: string
  postalCode: string
  latitude: number | null
  longitude: number | null
}

interface Suggestion { id: string; name: string; sub: string | null }

/**
 * Whether this deployment has a Places key, asked once per page load.
 *
 * At module scope rather than in state because the answer cannot change
 * while the tab is open, and a form that asks again every time it opens is
 * one wasted request per property somebody adds.
 */
let configured: boolean | null = null
let asking: Promise<boolean> | null = null

export function placesAvailable(): Promise<boolean> {
  if (configured !== null) return Promise.resolve(configured)
  asking ??= request<{ configured: boolean }>('/places/status')
    .then((result) => { configured = result.configured; return configured })
    .catch(() => { configured = false; return false })
  return asking
}

/**
 * Forget the answer above.
 *
 * Exported for the tests, which need a deployment with a key and one without
 * inside a single page, where the app only ever has one of the two. Nothing
 * in the product calls it.
 */
export function resetPlacesAvailability(): void {
  configured = null
  asking = null
}

/** Long enough that a fast typist makes one request, not eight. */
const DEBOUNCE_MS = 250
const ROW_HEIGHT = 42

export function AddressAutocomplete({
  id, describedBy, invalid, value, placeholder, onChange, onChoose,
}: {
  id?: string
  describedBy?: string
  invalid?: boolean
  /** The street line, which is what is typed into. */
  value: string
  placeholder?: string
  /** Every keystroke, so the line is a plain controlled input. */
  onChange: (line1: string) => void
  /** A suggestion taken: the whole address, for the caller to spread. */
  onChoose: (address: ChosenAddress) => void
}) {
  const [items, setItems] = useState<Suggestion[]>([])
  const [active, setActive] = useState(0)
  const [open, setOpen] = useState(false)

  const field = useRef<HTMLSpanElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  /** Google's, for the billing run — see the note at the top. */
  const session = useRef<string | null>(null)
  const [box, setBox] = useState<
    { left: number; top: number; width: number; maxHeight: number } | null>(null)

  const listId = `${id ?? 'address'}-suggestions`

  const close = useCallback(() => { setOpen(false); setItems([]) }, [])

  /*
   * Ask, debounced, and only while the menu is meant to be up. The timer is
   * cleared on every change, so the request goes when typing pauses rather
   * than per keystroke — which is both the polite thing to do to a billed
   * API and the only way the menu is not a strobe light.
   */
  useEffect(() => {
    if (!open) return
    const wanted = value.trim()
    if (wanted.length < 3) { setItems([]); return }

    let live = true
    const timer = window.setTimeout(() => {
      void placesAvailable().then((ok) => {
        if (!ok || !live) return
        session.current ??= uuidv7()
        return request<{ items: Suggestion[] }>(
          `/places/suggest?q=${encodeURIComponent(wanted)}&session=${session.current}`,
        ).then((result) => {
          if (!live) return
          setItems(result.items)
          setActive(0)
        })
      }).catch(() => { if (live) setItems([]) })
    }, DEBOUNCE_MS)

    return () => { live = false; window.clearTimeout(timer) }
  }, [open, value])

  useEffect(() => {
    if (!open) return
    const outside = (event: MouseEvent) => {
      const target = event.target as Node
      if (field.current?.contains(target) || panel.current?.contains(target)) return
      close()
    }
    document.addEventListener('mousedown', outside)
    return () => document.removeEventListener('mousedown', outside)
  })

  useLayoutEffect(() => {
    if (!open || items.length === 0) { setBox(null); return }
    const place = () => {
      const rect = field.current?.getBoundingClientRect()
      if (!rect) return
      const at = placeFloating({
        anchor: rect,
        width: rect.width,
        wanted: Math.min((panel.current?.scrollHeight ?? 0) || items.length * ROW_HEIGHT,
          6 * ROW_HEIGHT),
        floor: 2 * ROW_HEIGHT,
      })
      setBox({ left: at.left, top: at.top, width: rect.width, maxHeight: at.maxHeight })
    }
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, items.length])

  async function take(item: Suggestion) {
    const token = session.current ?? uuidv7()
    close()
    /*
     * The token's run ends with this call, whether or not it works — reusing
     * it for the next address would put two people's typing in one billing
     * session, which is not what it means.
     */
    session.current = null

    const found = await request<{ address: ChosenAddress | null }>(
      `/places/${encodeURIComponent(item.id)}?session=${token}`,
    ).catch(() => ({ address: null }))

    /*
     * If the details call comes back empty the street line still takes what
     * the row said. Somebody who picked "41 Grantley Rd" and got nothing at
     * all would reasonably think the product was broken.
     */
    if (found.address) onChoose(found.address)
    else onChange(item.name)
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (!open || items.length === 0) return
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActive((at) => (at + step + items.length) % items.length)
      return
    }
    if (event.key === 'Enter') {
      const item = items[active]
      if (!item) return
      event.preventDefault()
      void take(item)
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      close()
    }
  }

  return (
    <>
      <span ref={field} className="block">
        <TextInput
          id={id}
          describedBy={describedBy}
          invalid={invalid}
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={open && items.length > 0}
          aria-controls={open && items.length > 0 ? listId : undefined}
          aria-autocomplete="list"
          onChange={(event) => { onChange(event.target.value); setOpen(true) }}
          onKeyDown={onKeyDown}
        />
      </span>

      {open && items.length > 0 && box && createPortal(
        <div
          ref={panel}
          id={listId}
          role="listbox"
          aria-label="Address suggestions"
          style={{
            left: box.left, top: box.top, width: box.width, maxHeight: box.maxHeight,
          }}
          onMouseDown={(event) => event.preventDefault()}
          className={cn('fixed z-[80] overflow-y-auto rounded-popup border border-line',
            'bg-surface py-1.5 shadow-popover')}
        >
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              role="option"
              aria-selected={index === active}
              onMouseEnter={() => setActive(index)}
              onClick={() => void take(item)}
              style={{ minHeight: ROW_HEIGHT }}
              className={cn('mx-1.5 flex w-[calc(100%-12px)] items-center gap-2.5',
                'rounded-[8px] px-3 py-2 text-left',
                index === active ? 'bg-accent-soft' : 'hover:bg-hover')}
            >
              <span className={cn('flex h-[26px] w-[26px] shrink-0 items-center justify-center',
                'rounded-tile-small bg-line-soft text-text-body')}>
                <IconPin size={14} />
              </span>
              <span className="flex min-w-0 flex-col gap-px">
                <b className="truncate text-[13.5px] font-bold text-text">{item.name}</b>
                {item.sub && (
                  <span className="truncate text-fieldlabel text-text-muted">{item.sub}</span>
                )}
              </span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  )
}
